/**
 * PlacedCopyCommand
 *
 * A placed copy of a library asset entering or leaving the scene, as one undo
 * entry (#950). Undo and redo put the copy back exactly as it was: its own
 * nodes, their identities, and the library references among them.
 *
 * `CreateObjectCommand` and `DeleteObjectCommand` rebuild a subtree from data,
 * and put a library reference back as one: marked, and loaded again (see
 * `referenceMarks.ts`). This command wraps one of them and answers what a copy
 * needs beyond that.
 *
 * - **The seat.** A create appends the copy it rebuilds to its parent, so a copy
 *   that stood between two siblings is put back between them here. A delete
 *   seats its own rebuild (it records where the object stood) and the seat here
 *   then finds the copy in place.
 * - **Its own nodes.** The recorded set is the copy's nodes: the root and what
 *   stands under it, without what a reference derives (`ownNodes`, by the rule
 *   the create and delete commands share). That is not the scene's, and it comes
 *   back with the reference.
 *
 * Serialized identities need no help. The rebuilt nodes take the uids they were
 * recorded with because the engine keeps a uid nobody else holds, and nobody
 * does: an operation that swaps one copy for another retires the first before
 * the second is rebuilt, in both directions (the order of its steps, not this
 * command, guarantees that).
 *
 * Live identity is another matter, and a swap has to say it. Interaction
 * targets and the like hold a node by the shared ref of `objectIdentityRegistry`
 * (REFERENCE_IDENTITY.md), and a ref stands for one node at a time: it is bound
 * to the copy that stands and unbound when that copy goes. A copy placed in the
 * seat of one just removed ({@link PlacedCopyCommand.replacing}) takes over the
 * removed root's ref, so the wrapper is the same wrapper to whoever holds it
 * before, after, and after an undo or a redo.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import type { TraceScene } from '../../core/TraceScene';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import { invalidateNearestLayout } from '../../behaviors/layout';
import { CreateObjectCommand } from './CreateObjectCommand';
import { DeleteObjectCommand } from './DeleteObjectCommand';
import { seatAmongSiblings } from './ReparentCommand';
import { isDerived } from './referenceMarks';
import { deserializeCommand, registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'PlacedCopy';

/** One of the copy's own nodes, as the command finds it again. */
interface HeldNode {
    /** Shared with the wrapped command, which rebinds it on every rebuild. */
    ref: SceneObjectRef;
    /** Where the node stood when recorded; what a replayed command finds it by. */
    path: string;
}

interface PlacedCopyPayload {
    inner: SerializedCommand;
    recreatedBy: 'undo' | 'redo';
    index: number;
    nodes: Array<{ path: string }>;
}

export interface PlacedCopyOptions {
    /**
     * Seat the copy at this index among its parent's children, now and on
     * every redo. Default: where it stands.
     */
    index?: number;
}

/**
 * The nodes of a copy that are the SCENE's: the root and what stands under it,
 * parents before children, without what a reference derives. What a reference
 * derives beneath it is rebuilt by loading its document, never from data (a
 * node made from data under a reference would also be refused: the subtree is
 * derived, `canParentUnder`). The rule is the one the create and delete
 * commands apply to a list they are handed ({@link isDerived}), so a copy's own
 * nodes are what those commands would keep of all its descendants.
 */
export function ownNodes(root: SceneObject): SceneObject[] {
    const derived = new Set<SceneObject>();
    return [root, ...root.getDescendants()].filter((object, i) => {
        if (i > 0 && isDerived(object.getParent(), derived)) {
            derived.add(object);
            return false;
        }
        return true;
    });
}

/**
 * A node's data as it stands NOW: the live transform and the live behaviors
 * folded in, as every save and every delete flow does before it reads the
 * record.
 */
function dataNow(object: SceneObject): SceneObjectData {
    object.refreshSceneData();
    return object.getSceneObjectData();
}

function hold(nodes: SceneObject[]): HeldNode[] {
    return nodes.map(object => ({
        ref: object.traceScene.objectIdentityRegistry.refFor(object),
        path: object.getPath(),
    }));
}

/** A live (not disposed) instance, else null. */
function live(object: SceneObject | null): SceneObject | null {
    if (!object) return null;
    return object.transform.isDisposed() ? null : object;
}

export class PlacedCopyCommand implements UndoCommand {
    readonly label: string;
    private scene: TraceScene;
    private inner: CreateObjectCommand | DeleteObjectCommand;
    /** Which direction rebuilds the copy: redo for a placement, undo for a removal. */
    private recreatedBy: 'undo' | 'redo';
    /** The copy's own nodes, root first. */
    private nodes: HeldNode[];
    private index: number;

    private constructor(
        scene: TraceScene,
        inner: CreateObjectCommand | DeleteObjectCommand,
        recreatedBy: 'undo' | 'redo',
        nodes: HeldNode[],
        index: number,
    ) {
        this.scene = scene;
        this.inner = inner;
        this.recreatedBy = recreatedBy;
        this.nodes = nodes;
        this.index = index;
        this.label = inner.label;
    }

    /**
     * Record a copy that has just been placed: undo removes it, redo builds it
     * again from what it is now.
     *
     * Call it while the copy is in the scene and before anything moves into it
     * — what is recorded is the copy's own nodes, and whatever stands under
     * the copy at that moment is taken to be them.
     */
    static placed(root: SceneObject, options: PlacedCopyOptions = {}): PlacedCopyCommand {
        const scene = root.traceScene;
        const parent = root.getParent();
        if (!parent) throw new Error(`[PlacedCopyCommand] "${root.name}" has no parent`);
        if (options.index !== undefined) {
            seatAmongSiblings(parent, root, options.index);
            invalidateNearestLayout(parent);
        }
        const own = ownNodes(root);
        const create = new CreateObjectCommand(
            root,
            parent,
            dataNow(root),
            own.slice(1).map(object => ({ object, data: dataNow(object), parent: object.getParent() })),
        );
        return new PlacedCopyCommand(scene, create, 'redo', hold(own), parent.getChildren().indexOf(root));
    }

    /**
     * Take a copy out of the scene: the copy is deleted here, and undo puts it
     * back where it stood, references rebuilt.
     *
     * Whatever should outlive the copy (a slot's content) has to be moved out
     * first: the delete takes everything under the root with it.
     */
    static remove(root: SceneObject): PlacedCopyCommand {
        const scene = root.traceScene;
        const parent = root.getParent();
        if (!parent) throw new Error(`[PlacedCopyCommand] "${root.name}" has no parent`);
        const index = parent.getChildren().indexOf(root);
        const own = ownNodes(root);
        // Construction before delete: the entries are read while the objects
        // are live (DeleteObjectCommand's header).
        const del = new DeleteObjectCommand(
            root,
            own.map(object => ({ object, data: dataNow(object), parent: object.getParent() })),
            parent,
        );
        const nodes = hold(own);
        scene.deleteSceneObject(root);
        // Parity with DeleteObjectCommand.redo: a direct delete must not leave
        // the refs bound to disposed instances.
        for (const node of nodes) scene.objectIdentityRegistry.rebind(node.ref, null);
        invalidateNearestLayout(parent);
        return new PlacedCopyCommand(scene, del, 'undo', nodes, index);
    }

    /**
     * Record a copy placed in the seat of one just removed with
     * {@link PlacedCopyCommand.remove}: undo removes it, redo builds it again,
     * and its root is the removed root's identity. The removed root's ref is
     * bound to the new root here, and every ref taken for the root from now on
     * (the wrapped create's, this command's) is that one. Call it while the new
     * copy is in the scene and before anything moves into it, as for
     * {@link PlacedCopyCommand.placed}.
     */
    static replacing(removal: PlacedCopyCommand, root: SceneObject, options: PlacedCopyOptions = {}): PlacedCopyCommand {
        root.traceScene.objectIdentityRegistry.rebind(removal._rootIdentity(), root);
        return PlacedCopyCommand.placed(root, options);
    }

    /**
     * The ref that stands for the copy's root: what interaction targets and
     * every other command hold it by.
     * @internal
     */
    _rootIdentity(): SceneObjectRef {
        return this.inner._rootIdentity();
    }

    /**
     * Make a copy that is placed answer, at its root, to `ref`: the ref of the
     * root it replaces. For a command rebuilt from a recording, whose refs were
     * made before the copy it replaces was known.
     * @internal
     */
    _adoptRootIdentity(ref: SceneObjectRef): void {
        if (!(this.inner instanceof CreateObjectCommand)) {
            throw new Error('[PlacedCopyCommand] only a copy that is placed takes over an identity');
        }
        this.inner._adoptRootIdentity(ref);
        this.nodes[0].ref = ref;
    }

    /**
     * What stands under this copy's root now that is not the copy's own: what
     * an undo of the placement would delete along with it, the delete taking
     * everything under the root. The copy's own nodes are the ones recorded, and
     * what a reference derives beneath one is the copy's too; only the top of
     * anything else is named, since what stands under it goes with it.
     *
     * Empty for a removal, whose undo rebuilds the copy and deletes nothing, and
     * for a copy that is not standing. The nodes are found by the refs the command
     * holds for them, and not by the paths they were recorded at: a path can name
     * something else by now, and this must not report another object's children.
     */
    heldContent(): SceneObject[] {
        if (this.recreatedBy === 'undo') return [];
        const objects = this.nodes.map(node => live(node.ref.get()));
        const root = objects[0];
        if (!root) return [];
        const own = new Set(objects);
        const held: SceneObject[] = [];
        const visit = (object: SceneObject): void => {
            for (const child of object.getChildren()) {
                if (!own.has(child)) held.push(child);
                else if (!child.isSceneReference) visit(child);
            }
        };
        visit(root);
        return held;
    }

    // Neither wrapped command has a presentation side effect to silence beyond
    // the selection they always set, so `UndoOptions` has nothing to change here.
    async undo(): Promise<void> {
        await this.inner.undo();
        if (this.recreatedBy === 'undo') this.seat();
    }

    async redo(): Promise<void> {
        await this.inner.redo();
        if (this.recreatedBy === 'redo') this.seat();
    }

    serialize(): SerializedCommand {
        const payload: PlacedCopyPayload = {
            inner: this.inner.serialize(),
            recreatedBy: this.recreatedBy,
            index: this.index,
            nodes: this.nodes.map(({ path }) => ({ path })),
        };
        return { type: CODEC_TYPE, data: payload };
    }

    /**
     * What a wrapped create cannot do after it rebuilds the copy from data: put
     * it back between the siblings it stood between (a wrapped delete has done
     * it, and this finds the copy in place). Every node also takes up its
     * instance's shared ref here (see {@link find}).
     */
    private seat(): void {
        const root = this.nodes.map(node => this.find(node))[0];
        const parent = root?.getParent();
        if (root && parent) {
            seatAmongSiblings(parent, root, this.index);
            invalidateNearestLayout(parent);
        }
    }

    /**
     * The rebuilt instance of a node. A command built from a live copy finds it
     * through the ref the wrapped command rebinds; one rebuilt from a
     * recording has no such ref and finds it by the path it was recorded at.
     *
     * It then takes up the ref the instance already has, the one the wrapped
     * command bound and every command that names the instance shares. Binding
     * its own ref to the instance instead would replace that one in the
     * registry, and a command that asks for it later (the delete of a copy a
     * replayed step placed) would be handed a ref the placing command never
     * hears about again.
     */
    private find(node: HeldNode): SceneObject | null {
        const bound = live(node.ref.get());
        if (bound) return bound;
        const found = this.scene.resolveSceneObject(node.path);
        if (found) node.ref = this.scene.objectIdentityRegistry.refFor(found);
        return found;
    }

    /** Rebuild from a recording. `inner` must be the create or delete it wrapped. */
    static fromSerialized(scene: TraceScene, payload: PlacedCopyPayload): PlacedCopyCommand {
        const inner = deserializeCommand(payload.inner, scene);
        if (!(inner instanceof CreateObjectCommand) && !(inner instanceof DeleteObjectCommand)) {
            throw new Error(`[PlacedCopyCommand] "${payload.inner.type}" is not a create or a delete`);
        }
        const registry = scene.objectIdentityRegistry;
        return new PlacedCopyCommand(
            scene,
            inner,
            payload.recreatedBy,
            payload.nodes.map(node => ({ ref: registry.allocateUnbound(), ...node })),
            payload.index,
        );
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): PlacedCopyCommand =>
    PlacedCopyCommand.fromSerialized(scene, data as PlacedCopyPayload));
